import { html, LitElement, unsafeCSS } from "lit-element"
import { styleMap } from "lit-html/directives/style-map"
import style from "./grid.css"
import commonStyle from "../common.css"
import { Context, Rect } from "./common"
import Grid from "./grid"
import { isMobile } from "../utils"

// function debounce (func, wait) {
// 	let timeout
// 	return function (...args) {
// 		const context = this
// 		clearTimeout(timeout)
// 		timeout = setTimeout(() => {
// 			func.apply(context, args)
// 		}, wait)
// 	}
// }

const isLeftClick = e => e.button === 0
const isRightClick = e => e.button === 2

/**
 * @typedef GridProps
 * @property {number?} height
 * @property {number?} width
 */
/**
 * Gridコンポーネント
 */
export default class extends LitElement {
	static styles = [unsafeCSS(style), unsafeCSS(commonStyle)]
	static get properties () {
		return {
			width: { type: String },
			height: { type: String },
			font: { type: String },
			fontSizePt: { type: Number, attribute: "font-size" },
			rowHeight: { type: Number, attribute: "row-height" },
			headerHeight: { type: Number, attribute: "header-height" },
			selectMode: { type: String, attribute: "select-mode" },
			showButton: { type: Boolean, attribute: "show-button" },
			buttonIcon: { type: String, attribute: "button-icon" }
		}
	}

	#isGridInitialized = false

	/**
	 * @type {GridProps}
	 */
	#gridContainerProps = {}

	/**
	 * @type {HTMLCanvasElement}
	 */
	#canvas = null

	/**
	 * @type {Grid}
	 */
	#grid

	#cursor = "default"

	#scrollInfo = { left: 0, top: 0 }

	#font = "Meiryo UI"
	#fontSizePt = 10
	#rowHeight = 30
	#headerHeight = 30
	#showButton = false
	#buttonIcon = "fas fa-edit"

	/**
	 * @typedef {Object} FilterInfo
	 * @property {import("./column/default").default} col
	 * @property {string[]} selectedValues
	 * @property {number} colOffsetLeft
	 */

	/**
	 * @type {FilterInfo}
	 */
	#tempFilterInfo = null

	constructor () {
		super()
		this.selectMode = "cell"
		this.height = "100%"
		this.width = "100%"
	}

	get font () {
		return this.#font
	}

	set font (val) {
		this.#font = val
		this.rerender()
	}

	get fontSizePt () {
		return this.#fontSizePt
	}

	set fontSizePt (val) {
		this.#fontSizePt = val
		this.rerender()
	}

	get rowHeight () {
		return this.#rowHeight
	}

	set rowHeight (val) {
		this.#rowHeight = val
		this.rerender()
	}

	get headerHeight () {
		return this.#headerHeight
	}

	set headerHeight (val) {
		this.#headerHeight = val
		this.rerender()
	}

	get showButton () {
		return this.#showButton
	}

	set showButton (val) {
		this.#showButton = val
		this.requestUpdate()
	}

	get buttonIcon () {
		return this.#buttonIcon
	}

	set buttonIcon (val) {
		this.#buttonIcon = val
	}

	/**
	 * @type {HTMLElement}
	 */
	get #scrollable () {
		return this.shadowRoot.getElementById("scrollable")
	}

	get #parent () {
		return this.shadowRoot.querySelector(".grid-wrapper")
	}

	get input () {
		return this.shadowRoot.querySelector(".select-rect")
	}

	get gridControl () {
		return this.shadowRoot.querySelector(".grid-control")
	}

	get gridButton () {
		return this.shadowRoot.querySelector(".grid-button")
	}

	get #currentCellRect () {
		return this.#grid ? this.#grid.currentCellRect : null
	}

	get #filterPopup () {
		return this.shadowRoot.querySelector("#filter")
	}

	render () {
		return html`
		<div class="grid-wrapper" style=${this.#parentStyle} @keydown="${this.#keydown}" @copy=${this.#copy} @paste=${this.#paste}>
			<canvas id="canvas" style=${this.#canvasStyle} width=${this.#gridContainerProps.width} height=${this.#gridContainerProps.height}></canvas>
			<div 
				id="scrollable"
				class="scrollable"
				@scroll="${this.#onScroll}"
				@click="${this.#onCanvasClick}"
				@mousemove="${this.#mouseMove}"
				@mousedown="${this.#mousedown}"
				@mouseup="${this.#dragEnd}"
				@mouseleave="${this.#dragEnd}"
				@contextmenu="${e => e.preventDefault()}"
			>
				<div class="scroll-endpoint" style=${this.#scrollEndpointStyle}></div>
				<input type class="grid-control" style=${this.#rectStyle} @focus="${this.#recreateInput}" @keydown="${this.#handleKey}">
				<div class="select-rect" style=${this.#rectStyle} @keydown="${this.#handleKey}" @mousedown="${this.#rectMousedown}" @click="${this.#recreateInput}"></div>
				<div class="grid-button" style="${this.#buttonStyle}">
					<bui-icon icon="${this.#buttonIcon}" class="hl icon-button" @click="${this.#buttonClick}"></bui-icon>
				</div>
			</div>
			<bui-popup id="filter" static position="bottom-center" absolute @popup-close=${this.#closeFilterPopup}>
				<div class="surface shadow filter" style=${styleMap({ "font-size": `${this.fontSizePt}pt` })}>
					<div class="bui-list dense">
					${this.#renderFilterInputs}
					</div>
					<div class="filter-acitons">
						${this.#tempFilterInfo && this.#tempFilterInfo.col.isFiltered ? html`<button is="bui-button" class="bui-button is-primary is-small" @click="${this.#onRemoveFilter}">解除</button>` : ""}
						<button is="bui-button" class="bui-button is-primary is-small" @click="${this.#onFilterApply}">適用</button>
					</div>
				</div>
			</bui-popup>
		</div>
		`
	}

	get #renderFilterInputs () {
		if (!this.#tempFilterInfo) return html``

		const {
			col,
			selectedValues
		} = this.#tempFilterInfo

		const {
			id,
			type
		} = col.options

		let keyValues = []
		if (type === "select") {
			keyValues = col.selectOptions
		} else {
			keyValues = this.#grid.originalRows
				.map(row => row.data[id])
				.filter((value, index, self) => self.indexOf(value) === index)
				.filter(value => value != null && value !== "")
				.map(v => ({ label: v, value: v }))
		}

		const onClick = e => {
			if (e.target.checked) {
				selectedValues.push(e.target.value)
			} else {
				const index = selectedValues.indexOf(e.target.value)
				if (index >= 0) selectedValues.splice(index, 1)
			}
		}
		return keyValues.map(({ value, label }) => html`
				<label class="bui-list-item hl" >
					<div class="matter-checkbox grid">
						<input 
							type="checkbox"
							?checked=${selectedValues.some(filteredValue => filteredValue === value)}
							value=${value}
							@change="${onClick}"
						/>
						<span>${label}</span>
					</div>
				</label>
			`)
	}

	firstUpdated () {
		this.#canvas = this.shadowRoot.getElementById("canvas")
		const ctx = new Context(this.#canvas, this)
		this.#grid = new Grid(ctx, {
			onSizeChanged: () => {
				this.rerender()
			},
			requestScroll: (dx, dy) => {
				const { top, left } = this.#scrollInfo
				this.#scrollable.scrollTo({ left: left + dx, top: top + dy })
				this.requestUpdate()
			},
			requestCursorChange: (cursor) => {
				if (this.#cursor !== cursor) {
					this.#cursor = cursor

					// アニメーションなどで不意にサイズが変わることがあるのを考慮する。
					const rect = this.#parent.getBoundingClientRect()
					const d2pRatio = window.devicePixelRatio
					if (this.#gridContainerProps.width !== rect.width * d2pRatio ||
						this.#gridContainerProps.height !== rect.height * d2pRatio
					) {
						// サイズが変わっていたらグリッドごと再レンダリング
						this.rerender()
					} else {
						this.requestUpdate()
					}
				}
			},
			onFilterClicked: (offsetLeft, col) => {
				this.#tempFilterInfo = {
					col,
					selectedValues: [...col.filteredValues],
					offsetLeft
				}
				this.#filterPopup.showAtPoint(offsetLeft, this.headerHeight)
				this.#cursor = "default"
			}
		})

		this.#fitToParentSize()
	}

	get #parentStyle () {
		return styleMap({
			cursor: this.#cursor,
			height: this.height,
			width: this.width
		})
	}

	get #scrollEndpointStyle () {
		if (!this.#grid) return styleMap({})
		return styleMap({
			top: `${this.#grid.height}px`,
			left: `${this.#grid.width}px`
		})
	}

	get #canvasStyle () {
		if (!this.#parent) return styleMap({})
		const rect = this.#parent.getBoundingClientRect()
		return styleMap({
			position: "absolute",
			top: "0px",
			left: "0px",
			width: `${Math.floor(rect.width)}px`,
			height: `${Math.floor(rect.height)}px`
		})
	}

	get #rectStyle () {
		if (!this.#currentCellRect) return styleMap({})
		const rect = this.#currentCellRect
		return styleMap({
			display: "block",
			top: `${rect.top}px`,
			left: `${rect.left}px`,
			height: `${rect.height}px`,
			width: `${rect.width}px`
		})
	}

	get #buttonStyle () {
		if (!this.#currentCellRect) return styleMap({ display: "none" })
		const col = this.#grid.allCols.shift()
		const btnWidth = col ? col.options.width : 30

		const rect = this.#currentCellRect
		const display = this.#showButton && rect.top - this.#scrollable.scrollTop >= this.#headerHeight
		return styleMap({
			display: display ? "block" : "none",
			top: `${rect.top}px`,
			left: `${this.#scrollable.scrollLeft}px`,
			height: `${rect.height}px`,
			width: `${btnWidth}px`,
			lineHeight: `${rect.height}px`
		})
	}

	get visibleRect () {
		const parent = this.#scrollable
		return new Rect({
			top: this.#scrollInfo.top,
			left: this.#scrollInfo.left,
			height: parent.offsetHeight,
			width: parent.offsetWidth
		})
	}

	get gridHasFocus () {
		return this === document.activeElement
	}

	#onScroll (e) {
		const scrollable = this.#scrollable
		this.#scrollInfo = { left: scrollable.scrollLeft, top: scrollable.scrollTop }
		this.renderGrid()
		this.#grid.clearInput()
		this.isScrolling = true
		if (this.shadowRoot.activeElement !== this.gridControl && !isMobile) { // モバイルでフォーカスするとキーボードが出てきてしまう
			this.gridControl.focus()
		}
		this.isScrolling = false

		// スクロールイベント発火
		this.dispatchEvent(new CustomEvent("scroll"))
		this.rerender()
	}

	#onFilterApply () {
		if (this.#tempFilterInfo) {
			this.#tempFilterInfo.col.filteredValues = this.#tempFilterInfo.selectedValues
			this.#grid.applySortAndFilter()
			this.rerender()
		}
		this.#filterPopup.hide()
	}

	#onRemoveFilter () {
		if (this.#tempFilterInfo) {
			this.#tempFilterInfo.col.filteredValues = []
			this.#grid.applySortAndFilter()
			this.rerender()
		}
		this.#filterPopup.hide()
	}

	#closeFilterPopup () {
		this.#tempFilterInfo = null
	}

	#fitToParentSize () {
		const rect = this.#parent.getBoundingClientRect()
		const d2pRatio = window.devicePixelRatio
		this.#gridContainerProps.width = Math.floor(rect.width * d2pRatio)
		this.#gridContainerProps.height = Math.floor(rect.height * d2pRatio)
		return this.requestUpdate()
	}

	#recreateInput = e => {
		// if (!this.#currentCellInfo) return
		// this.input.scrollIntoView({ inline: "center", block: "center" })
		// スクロール時は再生成しない
		if (isLeftClick(e) && !this.#grid.ctx.isCellEditing && !this.isScrolling) {
			this.#grid.createInput()
		}
	}

	#rectMousedown = e => {
		// canvasにかぶせてるselect-rect要素がクリックされた場合、select-rect要素をposition:relativeにしている関係で
		// canvas上でのマウス座標を取得できないので、カスタムイベントをこちらで発火する。
		if (isLeftClick(e) && !this.#grid.ctx.isCellEditing && !this.isScrolling) {
			this.#grid.createInput()
			this.dispatchEvent(new CustomEvent("cell-clicked", {
				detail: {
					cell: this.#grid.getCurrentCell()
				}
			}))
		}
		if (isRightClick(e)) {
			this.dispatchEvent(new CustomEvent("cell-rightclicked", {
				detail: {
					cell: this.#grid.getCurrentCell()
				}
			}))
		}
	}

	#isOnScrollBar = e => {
		if (!(e instanceof MouseEvent)) return false
		return e.offsetX > e.target.clientWidth || e.offsetY > e.target.clientHeight
	}

	/**
	 * @param {MouseEvent} e
	 */
	#onCanvasClick = e => {
		// dragstartに引っ越し
	}

	#mouseMove = e => {
		if (!this.#isGridInitialized) return
		if (e.target === e.currentTarget) { this.#grid.pointAt(e.offsetX, e.offsetY) }
	}

	/**
	 *
	 * @param {MouseEvent} e
	 */
	#mousedown = e => {
		if (!this.#isGridInitialized) return
		if (e.target === e.currentTarget) {
			if (isLeftClick(e) && !this.#isOnScrollBar(e)) {
				this.#grid.clickAt(e)
				this.rerender()
			}
			if (isRightClick(e)) {
				e.preventDefault()
				this.#grid.rightClickAt(e.offsetX, e.offsetY)
			}
		}
		this.#grid.dragStart(e.offsetX, e.offsetY)

		// TODO 現状、どこかmousedownされると表示中のテキストのblurが行われ、同時にcontrol要素にフォーカスされるため、
		// イベントを抑止する
		// if (isLeftClick(e)) {
		// 	e.stopPropagation()
		// }
	}

	#dragEnd = e => {
		if (!this.#isGridInitialized) return
		this.#grid.dragEnd()
	}

	renderGrid () {
		this.#grid.render(this.visibleRect)
	}

	/**
	 *
	 * @param {KeyboardEvent} e
	 */
	#handleKey = e => {
		if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Tab", "Enter"].includes(e.key)) {
			// カーソルキー, スペースでのスクロール、タブトラバーサルを抑止
			e.preventDefault()

			// グリッドの選択セルを移動
			let x = 0
			let y = 0
			switch (e.key) {
			case "ArrowUp":
				y--
				break
			case "ArrowDown":
			case "Enter":
				y++
				break
			case "ArrowLeft":
				x--
				break
			case "ArrowRight":
			case "Tab":
				x += e.shiftKey ? -1 : 1
			}
			this.#grid.moveCurrentCell(x, y)
			this.requestUpdate()
		}
	}

	#buttonClick = e => {
		this.dispatchEvent(new CustomEvent("button-click", {
			detail: {
				cell: this.#grid.getCurrentCell()
			}
		}))
	}

	#keydown = e => {
		// ESCキーを押された時にダイアログが閉じてしまうのでイベントバブリングを止める
		if (e.key === "Escape") {
			e.stopPropagation()
		}
		if (e.key === "Delete") {
			this.#grid.deleteSelectedBoundaryValue()
		}
	}

	#copy = e => {
		this.#grid.copy()
	}

	#paste = e => {
		this.#grid.paste()
	}

	/**
	 * 初期化処理
	 */
	init ({ columns, records, rowId, autoInsert }) {
		this.#isGridInitialized = true
		// firstUpdatedが呼び出される前にinitが呼び出されうるので、updateCompleteで待つ
		return this.updateComplete.then(() => {
			this.#grid.clearColors()
			this.#grid.clearErrors()
			this.#grid.rowIdField = rowId || "id"
			this.#grid.autoInsert = autoInsert
			this.#grid.deleteAllRow()

			this.#grid.setColumns(columns)
			if (records) {
				this.#grid.addRows(records)
			}
			this.#fitToParentSize()
		})
	}

	/**
	 * データクリア
	 */
	clear () {
		this.#grid.clearColors()
		this.#grid.clearErrors()
		this.#grid.deleteAllRow()
	}

	/**
	 * 行追加
	 * @param {any} record
	 */
	addRow (record) {
		this.#grid.addRow(record)
		this.rerender()
	}

	/**
	 * 行追加(複数)
	 * @param {any[]} records
	 */
	addRows (records) {
		this.#grid.addRows(records)
		this.rerender()
	}

	/**
	 * 行削除
	 * @param {number} rowIndex
	 */
	deleteRowByIndex (rowIndex) {
		this.#grid.deleteRowByIndex(rowIndex)
		this.clearSelect()
		this.rerender(true)
	}

	/**
	 * 行削除
	 * @param {number|string} rowId
	 */
	deleteRowById (rowId) {
		this.#grid.deleteRowById(rowId)
		this.clearSelect()
		this.rerender(true)
	}

	/**
	 * 全行削除
	 */
	deleteAllRows () {
		this.#grid.deleteAllRow()
		this.rerender(true)
	}

	/**
	 * 行数取得
	 */
	getRowCount () {
		return this.#grid.rows.length
	}

	/**
	 * 列単位でのソート
	 * @param {string} colId
	 * @param {boolean} isAsc
	 */
	sort (colId, isAsc) {
		this.#grid.resetSort()
		this.#grid.sort(colId, isAsc)
		this.rerender()
	}

	/**
	 * ソートを解除
	 */
	resetSort () {
		this.#grid.resetSort()
		this.#grid.applySortAndFilter()
		this.rerender()
	}

	/**
	 * 行検索
	 * @param {(PublicRow)=>boolean} predicate
	 */
	findRows (predicate) {
		return this.#grid.findRows(predicate)
	}

	findOriginalRows (predicate) {
		return this.#grid.findOriginalRows(predicate)
	}

	/**
	 * 行フィルター
	 * @param {(any)=>boolean} predicate
	 */
	filter (predicate) {
		this.#grid.addFilter(predicate)
	}

	removeFilter () {
		this.#grid.removeFilter()
	}

	getCell (rowIndex, colId) {
		return this.#grid.getCell(rowIndex, colId)
	}

	getCellById (rowId, colId) {
		return this.#grid.getCellById(rowId, colId)
	}

	getCellByPoint (x, y) {
		return this.#grid.getRowColAt(x, y)
	}

	getCurrentCell () {
		return this.#grid.getCurrentCell()
	}

	getSelectedRows () {
		return this.#grid.getSelectedRows()
	}

	getModifiedRows () {
		return this.#grid.getModifiedRows()
	}

	restartModifiationObserver () {
		this.#grid.modifiedRowIds.clear()
	}

	// 外に見せる関数ではないが、諸事情のため公開する
	getRowColAt (x, y) {
		return this.#grid.getRowColAt(x, y)
	}

	clearSelect () {
		this.#grid.clearSelect()
		// ボタン表示がある場合はそれも非表示にするため再レンダリングを行う
		if (this.#showButton) {
			this.requestUpdate()
		}
	}

	selectRowByIndex (rowIndex) {
		if (this.selectMode === "cell") return
		this.#grid.selectRowByIndex(rowIndex)
	}

	selectRowById (id) {
		if (this.selectMode === "cell") return
		this.#grid.selectRowById(id)
	}

	/**
	 * 列幅等のプロパティを変更した際に呼び出すための再描画イベント
	 */
	#alreadyRenderRequested = false
	rerender (needUpdateDom = false) {
		if (this.#alreadyRenderRequested || !this.#isGridInitialized) return
		this.#alreadyRenderRequested = true
		this.#fitToParentSize().then(() => {
			this.#alreadyRenderRequested = false
			if (!this.#grid) return
			this.renderGrid()
			if (needUpdateDom) {
				this.requestUpdate()
			}
		})
	}

	setColWidth (colId, width) {
		const found = this.#grid.allCols.find(col => col.id === colId)
		if (found) {
			found.options.width = width
			this.#grid.initGridWidth()
			this.rerender()
		}
	}

	/**
	 * カラムオプションを更新する。
	 * @param {string} colId
	 * @param {any} options
	 */
	updateColOptions (colId, options) {
		// 固定列
		let found = this.#grid.allColsWithUnvisible.find(col => col.id === colId)
		if (!found) {
			// ヘッダ描画用列
			found = this.#grid.allColsForHeader.find(col => col.id === colId)
		}
		if (found) {
			found.updateOptions(options)
			// グリッド全体のサイズが変わる変更の場合、幅を再計算
			if (options.hidden != null || options.width != null) {
				this.#grid.initGridWidth()
			}
			this.rerender()
		}
	}

	addErrors (errors) {
		this.#grid.addErrors(errors)
		this.rerender()
	}

	addErrorByIndex (index, colId, errors) {
		this.#grid.addErrorByIndex(index, [colId], errors)
	}

	clearErrors () {
		this.#grid.clearErrors()
		this.rerender()
	}

	setColor (rowId, colId, colorInfo) {
		this.#grid.setColor(rowId, colId, colorInfo)
		this.rerender()
	}

	clearColors () {
		this.#grid.clearColors()
		this.rerender()
	}

	setRowColor (rowId, colorInfo) {
		this.#grid.setRowColor(rowId, colorInfo)
		this.rerender()
	}

	getAllRows () {
		return [...this.#grid.rows.map(row => row.data)]
	}

	getAllOriginalRows () {
		return [...this.#grid.originalRows.map(row => row.data)]
	}

	/**
	 * 行単位で読み取り専用にする条件を設定する
	 * @param {(any) => boolean} rule
	 */
	setReadonlyRowRule (rule) {
		this.#grid.setReadonlyRowRule(rule)
	}

	// リサイズイベント関連
	#eventRemover

	connectedCallback () {
		super.connectedCallback()
		const onResize = () => {
			this.rerender()
		}
		// リサイズあるのはPCだけなのでパフォーマンス考慮したdebounceは不要
		// const onResize = debounce(() => {
		// 	this.#fitToParentSize().then(() => {
		// 		this.renderGrid()
		// 	})
		// }, 100)
		window.addEventListener("resize", onResize)

		this.#eventRemover = () => {
			window.removeEventListener("resize", onResize)
		}
	}

	disconnectedCallback () {
		super.disconnectedCallback()
		if (this.#eventRemover) this.#eventRemover()
	}
}
